import { useEffect, useState } from 'react';
import type { TaskCreateFormType, UserType } from '@econtractor/types';
import { useAuth } from 'context/AuthContext';
import {
    isoDateToEndMillis,
    isoDateToStartMillis,
    millisToIsoDate,
} from '../helpers/initNewTaskStruct';

interface UseNewTaskFormParams {
    values: TaskCreateFormType;
    setFieldValue: (field: string, value: unknown) => void;
}

const useNewTaskForm = ({ values, setFieldValue }: UseNewTaskFormParams) => {
    const { auth } = useAuth();
    const [showDescription, setShowDescription] = useState(false);
    const [activeTab, setActiveTab] = useState('general');

    const showFields = Boolean(values.taskTypeId);
    const currentUser = (auth?.user || null) as UserType | null;

    useEffect(() => {
        if (!values.taskTypeId) {
            setShowDescription(false);
            setActiveTab('general');
        }
    }, [values.taskTypeId]);

    const canAssignMyself = (current: UserType | null) =>
        Boolean(currentUser?.ID && current?.ID !== currentUser.ID);

    const assignMyself = (field: 'executor' | 'controller') => {
        if (!currentUser) return;
        setFieldValue(field, currentUser);
    };

    const handleEndDateChange = (isoDate: string) => {
        if (!isoDate) return;
        const endMillis = isoDateToEndMillis(isoDate, values.withTime, values.endTime);
        if (endMillis == null) return;
        setFieldValue('dateEnd', endMillis);
        if (!values.multiDay) {
            setFieldValue('dateStart', isoDateToStartMillis(isoDate));
        }
    };

    const handleStartDateChange = (isoDate: string) => {
        if (!isoDate) return;
        const startMillis = isoDateToStartMillis(isoDate);
        if (startMillis == null) return;
        setFieldValue('dateStart', startMillis);

        // Якщо початок пізніший за «виконати до» — підтягуємо дедлайн до дати початку
        const endStartOfDay = isoDateToStartMillis(millisToIsoDate(values.dateEnd));
        if (endStartOfDay != null && startMillis > endStartOfDay) {
            const endMillis = isoDateToEndMillis(isoDate, values.withTime, values.endTime);
            if (endMillis != null) setFieldValue('dateEnd', endMillis);
        }
    };

    const handleWithTimeChange = (checked: boolean) => {
        setFieldValue('withTime', checked);
        const endIso = millisToIsoDate(values.dateEnd);
        const endMillis = isoDateToEndMillis(endIso, checked, values.endTime);
        if (endMillis != null) setFieldValue('dateEnd', endMillis);
    };

    const handleEndTimeChange = (time: string) => {
        setFieldValue('endTime', time);
        const endIso = millisToIsoDate(values.dateEnd);
        const endMillis = isoDateToEndMillis(endIso, true, time);
        if (endMillis != null) setFieldValue('dateEnd', endMillis);
    };

    const handleMultiDayChange = (checked: boolean) => {
        setFieldValue('multiDay', checked);
        if (!checked) {
            setFieldValue('dateStart', isoDateToStartMillis(millisToIsoDate(values.dateEnd)));
        }
    };

    return {
        showFields,
        showDescription,
        setShowDescription,
        activeTab,
        setActiveTab,
        canAssignMyself,
        assignMyself,
        handleEndDateChange,
        handleStartDateChange,
        handleWithTimeChange,
        handleEndTimeChange,
        handleMultiDayChange,
    };
};

export default useNewTaskForm;
